// Daily cash management — COD session validation and flagging
import { useState, useCallback, useMemo } from "react";
import {
  Banknote,
  CheckCircle,
  Flag,
  AlertCircle,
  Smartphone,
  Target,
  TrendingUp,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { ModuleGuard } from "@/components/admin/modules/ModuleGuard";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { SearchInput } from "@/components/admin/shared/SearchInput";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  useDailyCash,
  useValidateCashSession,
  useFlagCashSession,
} from "@/hooks/admin/usePayments";
import type { DailyCashSession, CashSessionStatus } from "@/lib/admin-api/payment";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<CashSessionStatus, string> = {
  open: "bg-stone-100 text-stone-700 border-stone-200",
  pending_validation: "bg-amber-100 text-amber-800 border-amber-200",
  validated: "bg-emerald-100 text-emerald-800 border-emerald-200",
  flagged: "bg-red-100 text-red-800 border-red-200",
};

const STATUS_LABELS: Record<CashSessionStatus, string> = {
  open: "Ouverte",
  pending_validation: "En attente",
  validated: "Validée",
  flagged: "Anomalie",
};

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(amount: number): string {
  return `${amount.toLocaleString("fr-SN")} FCFA`;
}

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Flag session modal
// ---------------------------------------------------------------------------

interface FlagModalProps {
  readonly open: boolean;
  readonly session: DailyCashSession | null;
  readonly onClose: () => void;
  readonly onConfirm: (reason: string) => Promise<void>;
  readonly isFlagging: boolean;
}

function FlagModal({ open, session, onClose, onConfirm, isFlagging }: FlagModalProps) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setReason("");
    setError(null);
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(async () => {
    if (!reason.trim()) {
      setError("Veuillez préciser la nature de l'anomalie.");
      return;
    }
    setError(null);
    try {
      await onConfirm(reason.trim());
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [reason, onConfirm, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Signaler une anomalie"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isFlagging}>
            Annuler
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isFlagging}
            className="gap-1.5 bg-red-600 hover:bg-red-700"
          >
            {isFlagging && (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            Signaler
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-stone-600">
          Agent :{" "}
          <span className="font-semibold text-stone-900">
            {session?.agent_name}
          </span>{" "}
          — {session ? formatDate(session.date) : ""}
        </p>
        {session && session.discrepancy_fcfa !== 0 && (
          <p className="text-sm text-red-600">
            Écart :{" "}
            <span className="font-semibold">
              {formatFcfa(session.discrepancy_fcfa)}
            </span>
          </p>
        )}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Nature de l'anomalie
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Décrivez l'anomalie constatée…"
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function DailyCash() {
  const [statusFilter, setStatusFilter] = useState<CashSessionStatus | "">("");
  const [agentSearch, setAgentSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [flaggingSession, setFlaggingSession] = useState<DailyCashSession | null>(null);

  const params = useMemo(
    () => ({
      status: statusFilter || null,
      date_from: dateFrom || null,
      date_to: dateTo || null,
    }),
    [statusFilter, dateFrom, dateTo],
  );

  const { data, isLoading, isError, refetch } = useDailyCash(params);
  const validateSession = useValidateCashSession();
  const flagSession = useFlagCashSession();

  const allSessions = data?.sessions ?? [];
  const summary = data?.summary;

  const sessions = useMemo(() => {
    if (!agentSearch.trim()) return allSessions;
    const q = agentSearch.toLowerCase();
    return allSessions.filter((s) =>
      s.agent_name.toLowerCase().includes(q),
    );
  }, [allSessions, agentSearch]);

  // Cash vs Mobile money breakdown
  const cashBreakdown = useMemo(() => {
    const totalCash = sessions.reduce((sum, s) => sum + s.cod_collected_fcfa, 0);
    const totalMobile = sessions.reduce(
      (sum, s) => sum + (s.mobile_money_collected_fcfa ?? 0),
      0,
    );
    const totalExpected = sessions.reduce(
      (sum, s) => sum + (s.expected_total_fcfa ?? 0),
      0,
    );
    const totalActual = sessions.reduce(
      (sum, s) => sum + (s.actual_total_fcfa ?? 0),
      0,
    );
    return { totalCash, totalMobile, totalExpected, totalActual };
  }, [sessions]);

  const handleValidate = useCallback(
    (id: string) => {
      validateSession.mutate(id);
    },
    [validateSession],
  );

  const handleFlagConfirm = useCallback(
    async (reason: string) => {
      if (!flaggingSession) return;
      await flagSession.mutateAsync({ id: flaggingSession.id, reason });
      setFlaggingSession(null);
    },
    [flaggingSession, flagSession],
  );

  const columns: readonly ColumnDef<DailyCashSession>[] = useMemo(
    () => [
      {
        id: "agent_name",
        header: "Agent",
        cell: (s) => (
          <span className="font-medium text-stone-900">{s.agent_name}</span>
        ),
      },
      {
        id: "date",
        header: "Date",
        cell: (s) => (
          <span className="text-sm text-stone-500">{formatDate(s.date)}</span>
        ),
      },
      {
        id: "orders_count",
        header: "Commandes",
        cell: (s) => (
          <span className="text-sm text-stone-700">{s.orders_count ?? "—"}</span>
        ),
        mobileVisible: false,
      },
      {
        id: "cod_collected_fcfa",
        header: "Espèces",
        cell: (s) => (
          <span className="font-medium text-stone-900">
            {formatFcfa(s.cod_collected_fcfa)}
          </span>
        ),
      },
      {
        id: "mobile_money_collected_fcfa",
        header: "Mobile Money",
        cell: (s) => (
          <span className="text-sm text-blue-700">
            {formatFcfa(s.mobile_money_collected_fcfa ?? 0)}
          </span>
        ),
        mobileVisible: false,
      },
      {
        id: "expected_total_fcfa",
        header: "Attendu",
        cell: (s) => (
          <span className="text-sm text-stone-600">
            {formatFcfa(s.expected_total_fcfa ?? 0)}
          </span>
        ),
        mobileVisible: false,
      },
      {
        id: "cod_handed_over_fcfa",
        header: "Remis",
        cell: (s) => (
          <span className="text-sm text-emerald-700">
            {formatFcfa(s.cod_handed_over_fcfa)}
          </span>
        ),
      },
      {
        id: "discrepancy_fcfa",
        header: "Écart",
        cell: (s) => (
          <span
            className={`text-sm font-semibold ${
              s.discrepancy_fcfa !== 0 ? "text-red-700" : "text-stone-400"
            }`}
          >
            {s.discrepancy_fcfa !== 0
              ? formatFcfa(s.discrepancy_fcfa)
              : "—"}
          </span>
        ),
      },
      {
        id: "status",
        header: "Statut",
        cell: (s) => (
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_COLORS[s.status]}`}
          >
            {STATUS_LABELS[s.status]}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (s) => (
          <div className="flex items-center gap-1">
            {(s.status === "pending_validation" || s.status === "open") && (
              <button
                type="button"
                onClick={() => handleValidate(s.id)}
                title="Valider la session"
                className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
              >
                <CheckCircle className="h-3.5 w-3.5" />
                Valider
              </button>
            )}
            {s.status !== "flagged" && s.status !== "validated" && (
              <button
                type="button"
                onClick={() => setFlaggingSession(s)}
                title="Signaler anomalie"
                className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
              >
                <Flag className="h-3.5 w-3.5" />
                Anomalie
              </button>
            )}
          </div>
        ),
      },
    ],
    [handleValidate],
  );

  return (
    <AdminLayout>
      <ModuleGuard moduleKey="cod-payments">
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Banknote className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Caisse journalière
            </h1>
            <p className="text-sm text-stone-500">
              {isLoading
                ? "Chargement…"
                : `${sessions.length} session(s) COD`}
            </p>
          </div>
        </div>

        {/* KPI row 1 — collection summary */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KPICard
            label="Collecté aujourd'hui"
            value={summary?.total_collected ?? 0}
            format="currency"
            icon={<Banknote className="h-4 w-4" />}
          />
          <KPICard
            label="Remis"
            value={summary?.total_handed_over ?? 0}
            format="currency"
            icon={<CheckCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Écarts"
            value={summary?.total_discrepancy ?? 0}
            format="currency"
            icon={<AlertCircle className="h-4 w-4" />}
          />
          <KPICard
            label="Sessions en attente"
            value={
              allSessions.filter(
                (s) =>
                  s.status === "pending_validation" || s.status === "open",
              ).length
            }
            format="number"
            icon={<Flag className="h-4 w-4" />}
          />
        </div>

        {/* Cash vs Mobile money breakdown */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div className="rounded-lg border border-stone-200 bg-white p-4">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                <Banknote className="h-4 w-4" />
              </div>
              <div>
                <p className="text-xs font-medium text-stone-500">Total espèces</p>
                <p className="font-display text-lg font-semibold text-stone-900">
                  {formatFcfa(cashBreakdown.totalCash)}
                </p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-stone-200 bg-white p-4">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <Smartphone className="h-4 w-4" />
              </div>
              <div>
                <p className="text-xs font-medium text-stone-500">Total Mobile Money</p>
                <p className="font-display text-lg font-semibold text-stone-900">
                  {formatFcfa(cashBreakdown.totalMobile)}
                </p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-stone-200 bg-white p-4">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-stone-50 text-stone-600">
                <Target className="h-4 w-4" />
              </div>
              <div>
                <p className="text-xs font-medium text-stone-500">Total attendu</p>
                <p className="font-display text-lg font-semibold text-stone-900">
                  {formatFcfa(cashBreakdown.totalExpected)}
                </p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-stone-200 bg-white p-4">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                <TrendingUp className="h-4 w-4" />
              </div>
              <div>
                <p className="text-xs font-medium text-stone-500">Total réel</p>
                <p className="font-display text-lg font-semibold text-stone-900">
                  {formatFcfa(cashBreakdown.totalActual)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-full sm:w-56">
            <SearchInput
              value={agentSearch}
              onChange={setAgentSearch}
              placeholder="Rechercher par agent…"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as CashSessionStatus | "")
            }
            className="h-9 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">Tous les statuts</option>
            <option value="open">Ouverte</option>
            <option value="pending_validation">En attente</option>
            <option value="validated">Validée</option>
            <option value="flagged">Anomalie</option>
          </select>

          {/* Date range */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-stone-600">Du :</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-stone-600">Au :</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="h-9 rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => {
                setDateFrom("");
                setDateTo("");
              }}
              className="text-sm text-stone-500 hover:text-stone-700"
            >
              Réinitialiser
            </button>
          )}
        </div>

        {/* Error state */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les sessions de caisse.</span>
            <button
              type="button"
              onClick={() => refetch()}
              className="ml-auto rounded border border-red-200 bg-white px-3 py-1 text-xs text-red-700 hover:bg-red-50"
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Table */}
        {!isError && (
          <>
            {!isLoading && sessions.length === 0 ? (
              <EmptyState
                icon={<Banknote className="h-6 w-6" />}
                title="Aucune session de caisse"
                description="Les sessions COD des agents apparaîtront ici."
              />
            ) : (
              <AdminDataTable
                data={sessions}
                columns={columns}
                isLoading={isLoading}
              />
            )}
          </>
        )}
      </div>

      {/* Flag modal */}
      <FlagModal
        open={flaggingSession !== null}
        session={flaggingSession}
        onClose={() => setFlaggingSession(null)}
        onConfirm={handleFlagConfirm}
        isFlagging={flagSession.isPending}
      />
      </ModuleGuard>
    </AdminLayout>
  );
}
